// Persisted Vision record read-side reader (spec 024 FR-005/FR-006, SC-006;
// 027 Slice B reader migration FR-009/FR-011, SC-004/SC-005).
//
// SOURCE OF TRUTH (027 Slice B): when the EngagementProvider has resolved a
// current engagement, `readVision()` reads over the provider's module-level
// SNAPSHOT (getEngagementSnapshot().vision) — the engagement is the aggregate
// root. When there is NO snapshot (getEngagementSnapshot() === null — first-run /
// safe state / no provider mounted yet) it falls back to the legacy
// localStorage["arcpilot.vision"] home (the SAME key VisionBoard's private
// `readStored` authors), so the read API + shape are byte-for-byte preserved
// pre- and post-migration. Either way it is the READ-ONLY reader used by the
// dashboard honesty gate: jsdom/SSR-safe and migration-tolerant — a missing /
// partial / garbage source NEVER throws and yields a safe empty shape
// ({ goals: [], coverStory: null }).
//
// Back-compat: a 021 record `{ seq, goals: Goal[] }` with no `coverStory` reads
// as { goals: [<mapped id/text>], coverStory: null }. A partial / garbage
// coverStory (not an object, or with no usable headline) MUST NOT surface as a
// confirmed headline — it collapses to null so the honesty gate holds.

import { getEngagementSnapshot } from '../hooks/useEngagement';

export const VISION_KEY = 'arcpilot.vision';

export interface CoverStory {
  headline: string;
  provenance: 'inferred' | 'stated';
  confirmed: boolean;
}

export interface ReadVisionGoal {
  id: string;
  text: string;
}

export interface Vision {
  goals: ReadVisionGoal[];
  coverStory: CoverStory | null;
}

function emptyVision(): Vision {
  return { goals: [], coverStory: null };
}

function readGoals(raw: unknown): ReadVisionGoal[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((g) => {
      if (!g || typeof g !== 'object') return null;
      const rec = g as { id?: unknown; text?: unknown };
      const id = typeof rec.id === 'string' ? rec.id : '';
      const text = typeof rec.text === 'string' ? rec.text : '';
      return { id, text };
    })
    .filter((g): g is ReadVisionGoal => g !== null);
}

function readCoverStory(raw: unknown): CoverStory | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as { headline?: unknown; provenance?: unknown; confirmed?: unknown };
  const headline = typeof rec.headline === 'string' ? rec.headline.trim() : '';
  // No usable headline → not a confirmed headline (honesty gate).
  if (!headline) return null;
  const provenance: 'inferred' | 'stated' = rec.provenance === 'stated' ? 'stated' : 'inferred';
  return {
    headline,
    provenance,
    confirmed: rec.confirmed === true,
  };
}

// Apply the (shared) defensive parse to an already-deserialised vision record,
// whether it came from the engagement snapshot or from localStorage JSON.
function parseVisionRecord(parsed: unknown): Vision {
  if (!parsed || typeof parsed !== 'object') return emptyVision();
  const record = parsed as { goals?: unknown; coverStory?: unknown };
  return {
    goals: readGoals(record.goals),
    coverStory: readCoverStory(record.coverStory),
  };
}

export function readVision(): Vision {
  // 027 Slice B: prefer the engagement snapshot when present (the aggregate
  // root). A missing snapshot falls back to the legacy localStorage home so the
  // pre-migration read shape is preserved exactly.
  const snapshot = getEngagementSnapshot();
  if (snapshot) {
    try {
      return parseVisionRecord(snapshot.vision);
    } catch {
      return emptyVision();
    }
  }
  try {
    const stored = window.localStorage.getItem(VISION_KEY);
    if (!stored) return emptyVision();
    return parseVisionRecord(JSON.parse(stored) as unknown);
  } catch {
    return emptyVision();
  }
}
